import React, { useCallback, useState } from 'react';
// import '../ModalTopUp/styles.scss';
import { Col, Row, Form } from 'antd';
import {
  MyAlert,
  MyButton,
  MyForm,
  MyInput,
  MyModal,
  NumberInput,
} from 'components/Atoms';
import cn from 'classnames';
import { useRequestFunds } from 'hooks/usePayment';
import { showModalSuccessfulScreen } from 'utilities/modal';
import { formatNumber } from 'utilities/stringHelper';
import { messageError } from 'utilities/message';
import { FormattedMessage, useIntl } from 'react-intl';
import { t } from 'utilities/lang';

const validation = {
  amount: [
    { required: true, message: <FormattedMessage id="wallet_topup.error.enter_amount" /> },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (value && value < 99) {
          return Promise.reject(<FormattedMessage id="wallet_topup.error.amount_min" values={{ min: 100 }} />);
        }
        return Promise.resolve();
      },
    }),
  ],
  reason: [{ required: true, message: <FormattedMessage id="wallet_topup.error.reason_required" /> }],
};

const amounts = [100, 200, 500, 1000, 2000, 3000];

const FormRequestFund = ({ form, handleClose, loading, setLoading }) => {
  const [selectedAmount, setSelectedAmount] = useState(undefined);
  const intl = useIntl();

  //services
  const requestFunds = useRequestFunds();

  const resetSelectedAmount = useCallback(() => {
    setSelectedAmount(undefined);
  }, []);

  const onClickBtnAmount = (value) => () => {
    if (loading) return;
    setSelectedAmount(value);
    form.setFieldsValue({
      amount: value,
    });
  };

  const onFinish = (values) => {
    setLoading(true);
    const amount = parseFloat(values.amount.toString().replace(',', ''));
    requestFunds.mutate(
      {
        ...values,
        amount,
      },
      {
        onSettled: () => {
          setLoading(false);
        },
        onSuccess: ({ data: { accountingAdminEmail } }) => {
          handleClose();

          if (!accountingAdminEmail) {
            messageError({
              content: intl.formatMessage({ id: "wallet_request_fund.error_admin_email_missing" }),
              key: 'accounting-admin-email-missing',
            });
          }

          showModalSuccessfulScreen({
            title: t('wallet_request_fund.success_title'),
            description: `${t('wallet_request_fund.success_desc', {
              amount: formatNumber(amount),
            })} ${
              !!accountingAdminEmail
                ? t('wallet_request_fund.success_admin_email', {
                    accountingAdminEmail,
                  })
                : ''
            }`,
            primaryButton: t('common.ok'),
          });
        },
      }
    );
  };

  return (
    <MyForm form={form} onFinish={onFinish}>
      <Form.Item label={intl.formatMessage({ id: "wallet_request_fund.amount" })} name="amount" rules={validation.amount}>
        <NumberInput
          placeholder={intl.formatMessage({ id: "wallet_request_fund.amount_placeholder" })}
          prefix="USD"
          disabled={loading}
          onKeyPress={resetSelectedAmount}
        />
      </Form.Item>
      <Row className="mb-20" gutter={[20, 10]}>
        {amounts.map((el) => (
          <Col key={el} span={8}>
            <MyButton
              type="outline"
              className={cn(
                'w-100p h-48 btn-outline-gray fz-18 primary-color amount-btn',
                { active: selectedAmount === el }
              )}
              onClick={onClickBtnAmount(el)}
            >
              {el}
            </MyButton>
          </Col>
        ))}
      </Row>
      <Form.Item
        className="mb-150"
        label={intl.formatMessage({ id: "wallet_request_fund.reason" })}
        name="reason"
        rules={validation.reason}
      >
        <MyInput placeholder={intl.formatMessage({ id: "wallet_request_fund.reason_placeholder" })} />
      </Form.Item>
    </MyForm>
  );
};

const ModalRequestFund = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleClose = useCallback(() => {
    form.resetFields();
    onClose();
  }, []);

  return (
    <MyModal
      width={400}
      visible={visible}
      onClose={!loading && handleClose}
      title={<FormattedMessage id="header.request_funds" />}
      footer={[
        <Row key="0" gutter={[10]}>
          <Col span={12}>
            <MyButton
              className="btn-secondary-outline w-100p h-45 primary-color"
              onClick={!loading && handleClose}
              disabled={loading}
            >
              <FormattedMessage id="common.cancel" />
            </MyButton>
          </Col>
          <Col span={12}>
            <MyButton
              loading={loading}
              type="primary"
              className="btn-primary-custom w-100p h-45"
              onClick={form.submit}
            >
              <FormattedMessage id="common.submit" />
            </MyButton>
          </Col>
        </Row>,
      ]}
    >
      <FormRequestFund
        form={form}
        handleClose={handleClose}
        loading={loading}
        setLoading={setLoading}
      />
    </MyModal>
  );
};

export default ModalRequestFund;
